import clsx from 'clsx';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { URL_REPLACEMENT } from '$const';
import { Button } from '~components/generic/editor/Button/Button';
import RouterLink from '~components/generic/RouterLink';
import { SettingsIcon } from '~components/icons';
import { SidebarUserToggleEditing } from '~components/sidebar/user/SidebarUserToggleEditing';
import useAppSelector from '~hooks/useAppSelector';
import usePermissions from '~hooks/usePermissions';
import { registerSLOHandlers } from '~services/api/calls/user';
import { NavigationStyle } from '~store/enums/navigation';
import { selectNavigationStyle } from '~store/selectors';
import replaceUrl from '~utils/replaceUrl';
import { ButtonLanguage } from '../../../sidebar/user/ButtonLanguage';
import { SettingsMenu } from '../../../sidebar/user/SettingsMenu';

export interface TopNavBarUserProps {
  isEditing: boolean;
  onEnableEditing: () => void;
  onDisableEditing: () => void;
  context: string | void;
  userId: number;
  currentLanguage: string;
  appLanguages: Language[];
  useAppLogin: boolean;
  isEditmodeDisabled: boolean;
}

export const NavbarHeaderUser = ({
  isEditing,
  onEnableEditing,
  onDisableEditing,
  context,
  isEditmodeDisabled,
  userId,
  currentLanguage,
  appLanguages,
  useAppLogin,
}: TopNavBarUserProps) => {
  const [settingsMenu, setSettingsMenu] = useState<{ x: number; y: number }>(undefined);
  const { hasToggleEditmodePermission } = usePermissions();
  const navigationStyle = useAppSelector(selectNavigationStyle);

  const btnSettingsClickHandler = useCallback((e: MouseEvent) => {
    const rect = (e.target as HTMLButtonElement).getBoundingClientRect();
    setSettingsMenu({ x: rect.right, y: rect.top + 35 });
  }, []);
  const handleSettingsMenuOnClose = useCallback(() => setSettingsMenu(undefined), []);

  const clsContainer = clsx('c-sidebar__user row align-items-center');
  const clsColumnSpacer = clsx('hidden');
  const clsButtonSettings = clsx('settings-button');

  // when the element is ready rendered, run initialize method of SLO again
  useEffect(registerSLOHandlers, []);

  return (
    <div className={clsContainer}>
      <div>
        {!userId && !useAppLogin && (
          <a href={replaceUrl(`${URL_REPLACEMENT.BACKEND_URL}/user/login`)} className="c-button c-button--dark">
            <FormattedMessage id="btnLabelLogin" defaultMessage="Login" />
          </a>
        )}
        {!userId && useAppLogin && (
          <RouterLink className="c-button c-button--dark" to="/user/login">
            <FormattedMessage id="btnLabelLogin" defaultMessage="Login" />
          </RouterLink>
        )}
        {!context && useAppLogin && (
          <RouterLink disabled={context} className="c-button c-button--dark" to="/user/logout">
            <FormattedMessage id="btnLabelLogout" defaultMessage="Logout" />
          </RouterLink>
        )}
        {!context && !useAppLogin && !!userId && navigationStyle === NavigationStyle.VERTICAL && (
          <a href={replaceUrl(`${URL_REPLACEMENT.BACKEND_URL}/user/logout`)} className="c-button c-button--dark">
            <FormattedMessage id="btnLabelLogout" defaultMessage="Logout" />
          </a>
        )}
        {!!context && (
          <button className="c-button c-button--dark" disabled>
            <FormattedMessage id="btnLabelLogout" defaultMessage="Logout" />
          </button>
        )}
      </div>
      <div className="ml-4">
        {!isEditmodeDisabled && hasToggleEditmodePermission && (
          <SidebarUserToggleEditing
            disabled={!!context}
            isEditing={isEditing}
            onEnableEditing={onEnableEditing}
            onDisableEditing={onDisableEditing}
          />
        )}
      </div>
      <div className="ml-4">
        <ButtonLanguage languages={appLanguages} currentLanguage={currentLanguage} />
      </div>
      <div className={clsColumnSpacer} />
      <div className="ml-4">
        <Button unflashy icon={<SettingsIcon />} className={clsButtonSettings} onClick={btnSettingsClickHandler} />
      </div>
      {settingsMenu && (
        <SettingsMenu
          navigationStyle={navigationStyle}
          position={'TOP_RIGHT'}
          userId={userId}
          pageX={settingsMenu.x}
          pageY={settingsMenu.y}
          onClose={handleSettingsMenuOnClose}
        />
      )}
    </div>
  );
};

export default NavbarHeaderUser;
